import { useState, useCallback, useEffect } from "react";
import { ScrollView } from "react-native";
import { View, Text } from "@src/components";
import { Stack, useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { useCameraRouteParams } from "@src/hooks/useCameraRouteParams";
import { FormInput } from "@src/components/FormInput";
import { Button } from "@src/components/Button";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import { useUpdateCameraNickname } from "@src/services/api/hooks";

export default function CameraDetailsScreen() {
  const { siteId, cameraKey, camera, isCameraLoading } = useCameraRouteParams({
    requireSiteId: true,
    requireCameraKey: true,
    fetchCamera: true,
  });

  const { theme } = useUnistyles();
  const router = useRouter();
  const updateNicknameMutation = useUpdateCameraNickname();

  // Use camera ID as the default value when no nickname is set
  const cameraId = cameraKey ?? "";
  const currentNickname = camera?.nickname ?? cameraId;

  const [displayName, setDisplayName] = useState(currentNickname);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local state when camera data loads
  useEffect(() => {
    const nicknameOrId = camera?.nickname ?? cameraId;
    setDisplayName(nicknameOrId);
    setHasChanges(false);
  }, [camera?.nickname, cameraId]);

  const handleDisplayNameChange = useCallback(
    (text: string) => {
      setDisplayName(text);
      setHasChanges(text !== currentNickname);
    },
    [currentNickname],
  );

  const handleSave = useCallback(() => {
    if (!cameraKey || !siteId) return;

    const nickname = displayName.trim() || null;

    updateNicknameMutation.mutate(
      {
        siteId,
        cameraKey,
        nickname,
      },
      {
        onSuccess: () => {
          showSuccessToast({ message: "Camera details updated", description: "Display name saved successfully" });
          router.back();
        },
        onError: (error: unknown) => {
          showErrorToast({ message: "Error", description: "Failed to update camera display name" });
          console.error("Update nickname error:", error);
        },
      },
    );
  }, [displayName, cameraKey, siteId, updateNicknameMutation, router]);

  const isLoading = isCameraLoading || updateNicknameMutation.isPending;

  return (
    <>
      <Stack.Screen
        options={{
          title: "Camera Details",
          headerStyle: {
            backgroundColor: theme.colors.background,
          },
          headerTintColor: theme.colors.text,
          headerBackButtonDisplayMode: "minimal",
        }}
      />
      <ScrollView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        contentContainerStyle={{
          padding: theme.spacing["5xl"],
          gap: theme.spacing["3xl"],
        }}
      >
        <View>
          <Text textStyle="sm/medium" color="text" mb="md">
            Display Name
          </Text>
          <Text textStyle="xs/regular" color="textMuted" mb="lg">
            Set a custom name for this camera to help identify it easily.
          </Text>
          <FormInput
            value={displayName}
            onChangeText={handleDisplayNameChange}
            placeholder="Camera display name"
            editable={!isLoading}
            autoCapitalize="words"
            maxLength={100}
          />
        </View>

        <View mt="xl">
          <Button
            onPress={handleSave}
            label="Save"
            variant="primary"
            size="md"
            loading={updateNicknameMutation.isPending}
            disabled={!hasChanges || isLoading}
          />
        </View>
      </ScrollView>
    </>
  );
}
